import React, { Component } from 'react';
import CustomTable from '../../custom/CustomTable';
import { ColumnProps } from 'primereact/column';
import CustomLoading from '../../custom/CustomLoading';
import LanguageUtil from '../../../util/LanguageUtil';
import {
  SafeListTableRow,
  WatchlistBaseModel,
  WatchlistIndividualRecord,
  WatchlistNameModel,
  WatchlistNameType,
} from '../../../domain/watchlist/WatchlistDomains';
import CustomPaginator, { CustomPaginatorProps } from '../../custom/CustomPaginator';
import CustomTableContentWrapper from '../../custom/CustomTableContentWrapper';
import moment from 'moment';
import _ from 'lodash';

class WatchlistSafeListTableComponent extends Component<Props, State> {
  public state = new State();

  public render() {
    const { rows, loading, selectedRows, onSelectionChange, onRowClick } = this.props;
    const { currentPage, pageRows } = this.state;
    const startIndex = (currentPage - 1) * pageRows;
    const endIndex = startIndex + pageRows;
    const currentData = rows.slice(startIndex, endIndex);

    if (loading) {
      return <CustomLoading />;
    }
    return (
      <div className={'w-full h-full flex flex-col overflow-hidden min-h-0'}>
        <div className={'overflow-y-auto flex-1 min-h-0'}>
          <CustomTable
            columns={this.getColumns()}
            value={currentData}
            selectionMode={'checkbox'}
            selection={selectedRows}
            onSelectionChange={(e) => this.onSelectionChange(e, startIndex, endIndex)}
            onRowClick={(e) => onRowClick(e.data as SafeListTableRow)}
          />
        </div>
        <div className={'paginator shrink-0 bg-white'}>
          <CustomPaginator
            rows={pageRows}
            totalRecords={rows.length}
            rowsPerPageOptions={[5, 10, 20, 50]}
            currentPage={currentPage}
            onPageChange={this.onPageChange}
            fixed={true}
            borderHidden={true}
          />
        </div>
      </div>
    );
  }

  public componentDidUpdate(prevProps: Props) {
    const { rows } = this.props;
    const { currentPage, pageRows } = this.state;
    if (prevProps.rows !== rows) {
      const totalPages = Math.max(1, Math.ceil(rows.length / pageRows) || 1);
      if (currentPage > totalPages) {
        this.setState({ currentPage: totalPages });
      }
    }
  }

  private onSelectionChange = (e: { value: SafeListTableRow[] }, startIndex: number, endIndex: number) => {
    const { rows, selectedRows, onSelectionChange } = this.props;
    const currentPageRecordIds = new Set(rows.slice(startIndex, endIndex).map((row) => row.consolidatedRecordId));
    const selectedOutsideCurrentPage = selectedRows.filter((row) => !currentPageRecordIds.has(row.consolidatedRecordId));
    onSelectionChange({ value: [...selectedOutsideCurrentPage, ...e.value] });
  };

  private onPageChange = (event: CustomPaginatorProps) => {
    this.setState({ pageRows: event.rows, currentPage: event.page });
  };

  private getColumns = (): ColumnProps[] => {
    return [
      {
        field: 'score',
        header: LanguageUtil.get({ id: 'pages.watchlist.table.score', defaultMessage: 'Score' }),
        className: 'min-w-[200px] max-w-[200px]',
        headerClassName: 'h-[44px] 2xl:h-[52px]',
        body: (row: SafeListTableRow) => {
          const score = row.record?.score;
          if (score == null) {
            return <div className={'flex items-center text-f-black-3 text-xs 2xl:text-md'}>N/A</div>;
          }
          return (
            <div className={`flex flex-col justify-center items-center ${this.getScoreItem(score)} min-w-[52px] w-[52px] rounded-[50px] h-[25px]`}>
              {score}
            </div>
          );
        },
      },
      {
        field: 'name',
        header: LanguageUtil.get({ id: 'pages.watchlist.table.name', defaultMessage: 'Name' }),
        className: 'w-1/3',
        sortable: false,
        body: (row: SafeListTableRow) => {
          return (
            <div className={'flex items-center text-f-black-3 text-xs 2xl:text-md'}>
              <div className={'w-8 h-8 flex items-center justify-center rounded-full bg-[#F4F3FF] mr-1'}>{this.getImage(row.record)}</div>
              {this.getName(row.record)}
            </div>
          );
        },
      },
      {
        field: 'category',
        header: LanguageUtil.get({ id: 'pages.watchlist.table.category', defaultMessage: 'Category' }),
        className: 'w-1/3',
        sortable: false,
        body: (row: SafeListTableRow) => {
          return (
            <div className={'flex items-center gap-x-2'}>
              {this.getCategory(row.record)?.map((value: string, index: number) => {
                return (
                  <span
                    key={index}
                    className={`flex text-xs shrink-0 2xl:text-md h-full justify-center px-[16px] py-[3px] items-center rounded-[50px] bg-[#F6F6F9] text-[#4F4F4F]`}
                  >
                    {value}
                  </span>
                );
              })}
            </div>
          );
        },
      },
      {
        field: 'level',
        header: 'Level',
        className: 'w-1/3',
        sortable: false,
        body: (row: SafeListTableRow) => {
          const record = row.record;
          return (
            <div className={'flex items-center text-f-black-3 text-xs 2xl:text-md'}>
              <CustomTableContentWrapper text={record?.level ? this.getLevel(record.level, record?.record?.recordType) : 'N/A'} />
            </div>
          );
        },
      },
      {
        field: 'createdAt',
        header: LanguageUtil.get({ id: 'pages.watchlist.table.createdAt', defaultMessage: 'Created Date' }),
        alignHeader: 'left',
        columnKey: 'createdAt',
        className: 'w-1/3',
        body: (row: SafeListTableRow) => {
          const record = row.record;
          return (
            <div className={'flex items-start text-f-black-3 text-xs 2xl:text-md'}>
              <CustomTableContentWrapper text={record?.record?.createdAt ? moment(record.record.createdAt).format('LLLL') : 'N/A'} />
            </div>
          );
        },
      },
    ];
  };

  private getLevel = (level: number, type: string): string => {
    if (type == 'INDIVIDUAL') {
      switch (level) {
        case 1:
          return 'Identification';
        case 2:
          return 'Name';
        case 3:
          return 'Name, Date of Birth';
        case 4:
          return 'Name, Citizenship';
        case 5:
          return 'Name, Date of Birth, Citizenship';
        default:
          return 'N/A';
      }
    } else if (type == 'ENTITY') {
      switch (level) {
        case 1:
          return 'Identifier';
        case 2:
          return 'Name';
        case 3:
          return 'Name, Date of Registration';
        default:
          return 'N/A';
      }
    } else {
      return 'N/A';
    }
  };

  private getCategory = (response: WatchlistBaseModel | null): string[] | undefined => {
    if (response == undefined || response == null) return undefined;
    const categories: string[][] = [];
    response.record.records.forEach((recordItem) => {
      categories.push(recordItem.watchlistRecord.categories);
    });
    return _.uniqBy([].concat(...categories) as string[], (value) => value);
  };

  private getScoreItem = (score: number): string => {
    if (score <= 25) {
      return 'bg-[#DBF8E5] text-[#3F9E2C]';
    } else if (score > 25 && score <= 50) {
      return 'bg-[#FCF1BB] text-[#C57500]';
    } else {
      return 'bg-[#FFF1F3] text-[#D2332F]';
    }
  };

  private getName = (record: WatchlistBaseModel | null): string => {
    if (record == undefined || record == null) {
      return 'N/A';
    }
    const flattenedArray: WatchlistNameModel[][] =
      record.record.records?.map((value) => {
        return value.watchlistRecord.names;
      }) || [];
    const namesData: WatchlistNameModel[] = ([] as WatchlistNameModel[]).concat(...flattenedArray);
    if (namesData.length === 0) {
      return LanguageUtil.get({ id: 'pages.watchlist.pdfReportComponent.nameUndefined', defaultMessage: 'Name Undefined' });
    }
    const longestPrimaryData: WatchlistNameModel[] = namesData.filter((item: WatchlistNameModel) => item.nameType === WatchlistNameType.PRIMARY);
    if (longestPrimaryData.length === 0) return namesData[0].value;
    const data: WatchlistNameModel = longestPrimaryData.reduce((a, b) => (a.value.length > b.value.length ? a : b));
    return data.value;
  };

  private getImage = (record: WatchlistBaseModel | null) => {
    if (record == undefined || record == null) {
      return <img src={'/icons/individual.svg'} alt='individual' />;
    }
    if (record.record.recordType == 'INDIVIDUAL') {
      const images = record.record?.records?.filter(
        (item) => (item.watchlistRecord as WatchlistIndividualRecord).images && (item.watchlistRecord as WatchlistIndividualRecord).images.length > 0,
      );
      if (images != undefined && images.length > 0) {
        return (
          <img
            src={(images[0].watchlistRecord as WatchlistIndividualRecord).images[0]}
            alt='-'
            className={'w-8 h-8 flex justify-center items-center rounded-[50%] bg-[#EFF2FE] object-cover'}
          />
        );
      }
      return <img src={'/icons/individual.svg'} alt='individual' />;
    }
    return <img src={'/icons/entity.svg'} alt='entity' />;
  };
}

interface Props {
  rows: SafeListTableRow[];
  loading: boolean;
  selectedRows: SafeListTableRow[];
  onSelectionChange: (e: { value: SafeListTableRow[] }) => void;
  onRowClick: (row: SafeListTableRow) => void;
}

class State {
  currentPage: number = 1;
  pageRows: number = 10;
}

export default WatchlistSafeListTableComponent;
